/**
 * Explore Map — views/default/post-types/page/layouts/explore_map.php
 *
 * A numbered list (accordion) and numbered map pins that select the same
 * items: one item open at a time; selecting the open item again closes it.
 * Every control for an item (its list trigger and each of its pins) carries
 * `data-explore-target="{index}"` and mirrors its state in `aria-expanded`.
 * The item gets `is-open`, its pins `is-active`; the open / close animation
 * is CSS (sass/default/layouts/_explore_map.scss).
 */

const controllers = new Set();

/**
 * @param {HTMLElement} root `[data-explore-map]`
 * @returns {AbortController}
 */
function setupExploreMap(root) {
    const abort = new AbortController();
    const items = [...root.querySelectorAll('[data-explore-item]')];
    const controls = [...root.querySelectorAll('[data-explore-target]')];

    /**
     * @param {number|null} index Item to open, or null to close all.
     */
    const select = (index) => {
        items.forEach((item) => {
            item.classList.toggle('is-open', Number(item.dataset.exploreItem) === index);
        });
        controls.forEach((control) => {
            const on = Number(control.dataset.exploreTarget) === index;
            control.setAttribute('aria-expanded', on ? 'true' : 'false');
            if (control.classList.contains('explore-map__pin')) {
                control.classList.toggle('is-active', on);
            }
        });
    };

    root.addEventListener('click', (event) => {
        const control = event.target.closest('[data-explore-target]');
        if (!control || !root.contains(control)) return;

        const index = Number(control.dataset.exploreTarget);
        const isOpen = items.some((item) => Number(item.dataset.exploreItem) === index && item.classList.contains('is-open'));
        select(isOpen ? null : index);
    }, { signal: abort.signal });

    return abort;
}

/**
 * @param {Document|HTMLElement} container Page or Barba container.
 */
export function initializeExploreMaps(container = document) {
    container.querySelectorAll('[data-explore-map]').forEach((root) => {
        if (root.dataset.exploreReady) return;
        root.dataset.exploreReady = 'true';
        controllers.add(setupExploreMap(root));
    });
}

export function destroyExploreMaps() {
    controllers.forEach((abort) => abort.abort());
    controllers.clear();
    document.querySelectorAll('[data-explore-map]').forEach((root) => delete root.dataset.exploreReady);
}
